/**
 * Retry helper for transient Firestore errors.
 *
 * Firestore occasionally rejects with retryable, transient failures such as
 * `[firestore/unavailable]` ("The service is currently unavailable. This is a
 * most likely a transient condition and may be corrected by retrying with a
 * backoff."). This wrapper re-runs the operation with exponential backoff for
 * those codes only, mirroring the axios `createNetworkRetryInterceptor` pattern
 * used for HTTP calls.
 *
 * Non-retryable errors (permission-denied, not-found, invalid-argument, the
 * app's own RequestTimeoutError, etc.) are re-thrown immediately.
 */

/**
 * Retryable Firestore status codes (server-side transient conditions).
 *
 * Codes are normalized to their short form because the native SDK
 * (@react-native-firebase) prefixes them with `firestore/`
 * (e.g. `firestore/unavailable`) while the web modular SDK uses the bare code
 * (e.g. `unavailable`).
 */
const RETRYABLE_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'internal',
  'resource-exhausted',
  'aborted',
  'cancelled',
]);

export const isRetryableFirestoreError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const code = (err as { code?: unknown }).code;
  if (typeof code !== 'string') return false;
  // Native SDK: "firestore/unavailable" → "unavailable"; web SDK: "unavailable".
  const normalized = code.includes('/') ? (code.split('/').pop() as string) : code;
  return RETRYABLE_CODES.has(normalized);
};

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

type RetryOptions = {
  retries?: number;
  baseDelayMs?: number;
};

/**
 * Run `factory` and retry with exponential backoff on transient Firestore
 * errors. `factory` must be a thunk so each attempt starts a fresh Firestore
 * operation (a settled promise cannot be re-awaited).
 *
 * @param factory        Thunk that performs one Firestore operation.
 * @param options.retries      Max retry attempts after the first try (default 2).
 * @param options.baseDelayMs  Base backoff delay in ms; grows as base × 2^attempt (default 500).
 */
export const withFirestoreRetry = async <T>(
  factory: () => Promise<T>,
  options?: RetryOptions,
): Promise<T> => {
  const maxRetries = options?.retries ?? 2;
  const baseDelayMs = options?.baseDelayMs ?? 500;

  let attempt = 0;
  for (;;) {
    try {
      return await factory();
    } catch (err) {
      if (attempt >= maxRetries || !isRetryableFirestoreError(err)) {
        throw err;
      }
      const delay = baseDelayMs * 2 ** attempt;
      attempt += 1;
      await sleep(delay);
    }
  }
};
